import { PriceRecord } from './types';
import { getAuthFromStorage } from './utils';

// API配置
const API_CONFIG = {
  // 使用相对路径，通过代理访问
  baseUrl: '/main-api', // 服务端API基础路径
  authUrl: '/auth-api', // 认证服务API基础路径
  endpoints: {
    list: '/api/prices',           // 获取价格列表
    detail: '/api/prices/:id',     // 获取单个价格记录详情
    create: '/api/prices',         // 创建新价格记录
    update: '/api/prices/:id',     // 更新价格记录
    delete: '/api/prices/:id',     // 删除价格记录
    login: '/auth/kc/users/login',      // 用户登录
    definedData: '/api/defined-data/',  // 获取预定义数据
    generateShareLink: '/api/share/generate', // 生成分享链接
    validateShareLink: '/api/share/validate', // 验证分享链接
    priceProductList: '/api/price-products/list', // 获取产品列表
    priceProductCollectableCount: '/api/price-products/collectable-count', // 获取可采集产品总数
  },
  uploadUrl: '/upload-api/api/upload' // 图片上传专用API
};

// 获取认证头信息
const getAuthHeaders = () => {
  const { token } = getAuthFromStorage();
  if (!token) return {};
  
  // 检查token是否已经包含"Bearer"前缀
  if (token.startsWith('Bearer ')) {
    return { 'Authorization': token };
  } else {
    return { 'Authorization': `Bearer ${token}` };
  }
};

// API请求封装
const api = {
  // 通用请求方法
  async request(endpoint: string, options: RequestInit = {}, useAuth = true) {
    try {
      const isAuthEndpoint = endpoint.startsWith('/auth');
      const baseUrl = isAuthEndpoint ? API_CONFIG.authUrl : API_CONFIG.baseUrl;
      const url = baseUrl + endpoint;
      
      // 添加认证头
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...options.headers as Record<string, string>
      };
      
      // 如果需要认证并且有token，添加认证头
      if (useAuth) {
        const authHeaders = getAuthHeaders();
        Object.assign(headers, authHeaders);
      }
      
      const response = await fetch(url, {
        ...options,
        headers,
        // 简化CORS配置，尝试不使用credentials
        mode: 'cors'
      });

      if (!response.ok) {
        let errorMessage = `HTTP错误: ${response.status}`;
        try {
          const errorData = await response.json();
          if (errorData && errorData.error) {
            errorMessage = errorData.error;
          } else if (errorData && errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (e) {
          // JSON解析失败，使用默认错误信息
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      return data; // 保持原有返回格式
    } catch (error) {
      console.error('API请求失败:', error);
      return { success: false, error: (error instanceof Error) ? error.message : '请求失败' };
    }
  },

  // 用户登录
  async login(employeeId: string, password: string) {
    return await this.request(API_CONFIG.endpoints.login, {
      method: 'POST',
      body: JSON.stringify({ username: employeeId, password: password })
    }, false);
  },

  // 获取价格记录列表
  async getPriceList(page = 1, limit = 10, employeeId?: string, shareId?: string) {
    try {
      let queryParams = `?page=${page}&limit=${limit}`;
      if (employeeId) {
        queryParams += `&employeeId=${employeeId}`;
      }
      if (shareId) {
        queryParams += `&shareId=${shareId}`;
      }
      
      const response = await this.request(API_CONFIG.endpoints.list + queryParams, {
        method: 'GET'
      });
      
      // 确保每条记录的tags属性是数组
      if (response.success && Array.isArray(response.data)) {
        response.data = response.data.map((record: any) => ({
          ...record,
          tags: typeof record.tags === 'string' 
            ? record.tags.split(',').filter((tag: string) => tag.trim() !== '') 
            : Array.isArray(record.tags) ? record.tags : []
        }));
      }
      
      return response;
    } catch (error) {
      console.error('获取价格列表失败:', error);
      return { success: false, error: (error instanceof Error) ? error.message : '获取数据失败' };
    }
  },

  // 创建新价格记录
  async createPrice(priceData: PriceRecord) {
    // 添加当前用户的employeeId
    const { user } = getAuthFromStorage();

    if (user && !priceData.employeeId) {
      priceData.employeeId = user.employeeId.toString();
    }
    //添加分享id
    priceData.shareId = localStorage.getItem('current_share_id') || '';

    return await this.request(API_CONFIG.endpoints.create, {
      method: 'POST',
      body: JSON.stringify(priceData)
    });
  },

  // 更新价格记录
  async updatePrice(id: string, priceData: PriceRecord) {
    console.log('更新价格记录，ID:', id, '数据:', priceData);
    const endpoint = API_CONFIG.endpoints.update.replace(':id', id);
    return await this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(priceData)
    });
  },

  // 删除价格记录
  async deletePrice(id: string) {
    const endpoint = API_CONFIG.endpoints.delete.replace(':id', id);
    return await this.request(endpoint, {
      method: 'DELETE'
    });
  },

  // 上传媒体文件
  async uploadMedia(files: File[]) {
    try {
      const urls = [];
      
      // 逐个上传文件
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        
        console.log(`上传文件: ${file.name}, 类型: ${file.type}`);
        
        // 简化上传请求配置
        const response = await fetch(API_CONFIG.uploadUrl, {
          method: 'POST',
          body: formData,
          // 不使用 credentials
          mode: 'cors'
        });
        
        if (!response.ok) {
          throw new Error(`上传失败，状态码: ${response.status}`);
        }
        
        const result = await response.json();
        console.log('上传响应:', result);
        
        // 从响应中提取URL
        if (result && result.viewUrl) {
          urls.push(result.viewUrl);
        } else {
          console.warn('响应中未找到viewUrl字段:', result);
          // 尝试寻找其他可能的URL字段
          if (result && result.url) {
            urls.push(result.url);
          } else if (result && result.data && result.data.url) {
            urls.push(result.data.url);
          } else {
            throw new Error('无法从上传响应中获取图片URL');
          }
        }
      }
      
      return { 
        success: true, 
        data: { urls } 
      };
    } catch (error) {
      console.error('上传媒体失败:', error);
      return { success: false, error: (error instanceof Error) ? error.message : '上传失败' };
    }
  },

  // 获取价格记录详情
  async getPriceDetail(id: string) {
    const endpoint = API_CONFIG.endpoints.detail.replace(':id', id);
    try {
      const response = await this.request(endpoint, {
        method: 'GET'
      });
      
      // 确保tags属性是数组
      if (response.success && response.data) {
        response.data.tags = typeof response.data.tags === 'string'
          ? response.data.tags.split(',').filter((tag: string) => tag.trim() !== '') 
          : Array.isArray(response.data.tags) ? response.data.tags : [];
      }
      
      return response;
    } catch (error) {
      return { success: false, error: (error instanceof Error) ? error.message : '获取数据失败' };
    }
  },

  // 获取商品列表数据
  async getDefinedDataRecords(category: string) {
    try {
      const { user } = getAuthFromStorage();
      if (!user) {
        return { success: false, error: '用户未登录' };
      }
      const createBy = user.employeeId.toString() + '-' + user.realname;
      const queryParams = `?create_by=${encodeURIComponent(createBy)}&category=${encodeURIComponent(category)}`;
      const response = await this.request(API_CONFIG.endpoints.priceProductList + queryParams, {
        method: 'GET'
      });
      return response;
    } catch (error) {
      return { success: false, error: (error instanceof Error) ? error.message : '获取商品列表失败' };
    }
  },
  
  // 获取定义数据
  async getDefinedData(definedType: string) {
    try {
      const queryParams = `?defined_type=${encodeURIComponent(definedType)}`;
      const response = await this.request(API_CONFIG.endpoints.definedData + queryParams, {
        method: 'GET'
      });
      return response;
    } catch (error) {
      console.error('获取定义数据失败:', error);
      return { success: false, error: (error instanceof Error) ? error.message : '获取数据失败' };
    }
  },
  
  // 获取可采集商品总数
  async getCollectableCount() {
    try {
      const { user } = getAuthFromStorage();
      if (!user) {
        return { success: false, error: '用户未登录' };
      }
      
      // 组装 create_by 参数
      const createBy = user.employeeId.toString() + '-' + user.realname;
      const queryParams = `?create_by=${encodeURIComponent(createBy)}`;
      const response = await this.request(API_CONFIG.endpoints.priceProductCollectableCount + queryParams, {
        method: 'GET'
      });
      return response;
    } catch (error) {
      return { success: false, error: (error instanceof Error) ? error.message : '获取可采集商品总数失败' };
    }
  },

  // 生成分享链接
  async generateShareLink() {
    try {
      const { user } = getAuthFromStorage();
      if (!user) {
        return { success: false, error: '用户未登录' };
      }
      
      const response = await this.request(API_CONFIG.endpoints.generateShareLink, {
        method: 'POST',
        body: JSON.stringify({
          employeeId: user.employeeId.toString() + "-" + user.realname
        })
      });
      return response;
    } catch (error) {
      return { success: false, error: (error instanceof Error) ? error.message : '生成分享链接失败' };
    }
  },
  
  // 验证分享链接
  async validateShareLink(shareId: string) {
    try {
      const response = await this.request(API_CONFIG.endpoints.validateShareLink, {
        method: 'POST',
        body: JSON.stringify({ shareId })
      }, false); // 不需要认证
      return response;
    } catch (error) {
      return { success: false, error: (error instanceof Error) ? error.message : '验证分享链接失败' };
    }
  }
};

export default api; 